/**
 * Hook to check subscription status and handle trial expiration
 */

import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAWSAuth } from '../contexts/AWSAuthContext';
import type { SubscriptionTier } from '../types/subscription';

export type SubscriptionStatus = 'trialing' | 'active' | 'past_due' | 'canceled' | 'trial_ended' | 'none';

export interface SubscriptionState {
  status: SubscriptionStatus;
  tier: SubscriptionTier;
  trialEndsAt: Date | null;
  isTrialActive: boolean;
  isTrialEnded: boolean;
  hasActiveSubscription: boolean;
  needsPaymentUpdate: boolean;
}

/**
 * Check if user has an active subscription or trial
 */
export function useSubscriptionStatus(): SubscriptionState {
  const { userProfile } = useAWSAuth();

  return useMemo(() => {
    if (!userProfile) {
      return {
        status: 'none',
        tier: 'plus',
        trialEndsAt: null,
        isTrialActive: false,
        isTrialEnded: false,
        hasActiveSubscription: false,
        needsPaymentUpdate: false,
      };
    }

    const status = (userProfile.subscriptionStatus || 'none') as SubscriptionStatus;
    const tier = userProfile.subscriptionTier || 'plus';
    const trialEndsAt = userProfile.trialEndsAt ? new Date(userProfile.trialEndsAt) : null;
    
    const now = new Date();
    const isTrialActive = !!(status === 'trialing' && trialEndsAt && trialEndsAt > now);
    const isTrialEnded = !!(status === 'trial_ended' || (status === 'trialing' && trialEndsAt && trialEndsAt <= now));
    const hasActiveSubscription = !!(status === 'active' || isTrialActive);
    const needsPaymentUpdate = status === 'past_due';

    return {
      status,
      tier,
      trialEndsAt,
      isTrialActive,
      isTrialEnded,
      hasActiveSubscription,
      needsPaymentUpdate,
    };
  }, [userProfile]);
}

/**
 * Hook to redirect to pricing if trial ended or no subscription
 */
export function useRequireSubscription(minTier: SubscriptionTier = 'plus', redirectTo: string = '/pricing') {
  const navigate = useNavigate();
  const subscriptionState = useSubscriptionStatus();
  const { userProfile } = useAWSAuth();

  useEffect(() => {
    if (!userProfile) return;

    const { isTrialEnded, hasActiveSubscription, needsPaymentUpdate } = subscriptionState;

    // If trial ended or no active subscription, redirect to pricing
    if (isTrialEnded || (!hasActiveSubscription && subscriptionState.status !== 'active')) {
      navigate(redirectTo, { replace: true });
      return;
    }

    // If payment is past due, show banner but don't block (handled by banner component)
    if (needsPaymentUpdate) {
      // Don't block, just show banner
      return;
    }

    // Check tier requirement
    const tierOrder: SubscriptionTier[] = ['plus', 'pro', 'elite'];
    const userTierIndex = tierOrder.indexOf(subscriptionState.tier);
    const requiredTierIndex = tierOrder.indexOf(minTier);

    if (userTierIndex < requiredTierIndex) {
      navigate(redirectTo, { replace: true });
    }
  }, [subscriptionState, userProfile, navigate, redirectTo, minTier]);

  return subscriptionState;
}

